import * as React from 'react';

import {
    useTranslation,
    useForm,
    G,
    useResolveName,
    useLoading,
    useSearch
} from '@components';
import {
    ISecurity, IWork
} from '@models';
import './RestrictContractor.scss';

interface IProps {
    security: ISecurity;
    selectedContractorId: number;
    data: IContractor[] | any;
    work: IWork;
    restrictContractor: Function;
    handleToast: Function;
    onClose: Function;
    legalForms: any;
    editRestriction: Function;
}

interface IContractor {
    id: number;
    activityTypeId?: number;
    activityTypeName: string;
    address: string;
    city: string;
    code: string;
    contactPerson: string;
    countryId: number;
    countyId: number;
    creationDate: string;
    currentStatusDate: string
    currentStatusType: number
    email: string[];
    endDate?: string;
    legalFormId: number
    legalFormName: string
    name: string
    otherActivityType?: string
    phone: string
    providerCode: string
    workId: number
}


interface IResult {
    id: number,
    machineryType: number
    machineryTypeName: string
    name: string
    licenseNumber: string
    code: string
    contractorName: string
    applicantName: string
    applicantEmail: string
    date: string
    reason: string
    remarks: string
    isActive: boolean
    deactivatedAt?: string
    workId: number
}

export default function RestrictContractor(props: IProps) {

    const { t } = useTranslation();

    const [isSaving, setIsSaving] = React.useState(false);
    const loading = useLoading(true);

    const [ contractor, setContractor ] = React.useState<IContractor & { isRestrictedContractor?: boolean }>( props.data.find((x: IContractor) => x.id === props.selectedContractorId) );
    const [ restrictionId, setRestrictionId ] = React.useState<number | undefined>(undefined);
    const [ applicantName, setApplicantName ] = React.useState<string>(props.security.user?.userName ?? '');
    const [ applicantEmail, setApplicantEmail ] = React.useState<string>(props.security.user?.email ?? '');
    const [ date, setDate ] = React.useState<string>(new Date().toISOString().split('T')[0]);
    const [ reason, setReason ] = React.useState<string>('');
    const [ remarks, setRemarks ] = React.useState<string>('');
    const [ disabled, setDisabled ] = React.useState(false);
    const [ canModify, setCanModify ] = React.useState<boolean>(false);

    const restriction = useSearch<IResult>({
                    workId: props.work.id,
                    search: 'contractors/restricted',
                    filters: {
                        workId: props.work.id,
                        contractorCode: contractor?.code,
                    },
                });

    const initialize = () => {
        const restrictionData = restriction.value[0];
        if (restrictionData) {
            setRestrictionId(restrictionData.id);
            setApplicantName(restrictionData.applicantName);
            setApplicantEmail(restrictionData.applicantEmail);
            setDate(restrictionData.date)
            setReason(restrictionData.reason);
            setRemarks(restrictionData.remarks ?? '');
        }
    }

    const calculateCanModify = () => {
        if (!contractor.isRestrictedContractor) {
            setCanModify(true);
        }
        if (props.security.isGestor()) {
            setCanModify(true);
        }
    }

    React.useEffect(() => {
        if(contractor?.isRestrictedContractor === true) {
            initialize();
        }
    }, [restriction.value]);

    React.useEffect(() => {
        calculateCanModify();
        if(contractor?.isRestrictedContractor === true) {
            restriction.doSearch();
        }
    }, []);

    const handleSave = async () => {
        setIsSaving(true);

        const timeoutPromise =  new Promise((_, reject) => {
            setTimeout(() => reject(new Error(t('restricted.contractor.workInProgress'))), 5000);
          });

          const respPromise = new Promise((resolve, reject) => {
            const resp = props.restrictContractor(
                props.work.id,
                contractor?.name,
                contractor?.code,
                contractor?.legalFormId,
                contractor?.providerCode,
                contractor?.contactPerson,
                contractor?.email[0],
                contractor?.phone,
                contractor?.id,
                applicantName,
                applicantEmail,
                reason,
                remarks
            );
            resolve(resp);
          });

        Promise.race([respPromise, timeoutPromise]).then((value : any) => {
            if (value.isError === false && typeof value.value == 'number') {
                props.handleToast(t('Contractor restricted successfully'), true);
            } else {
                props.handleToast(t('Error restricting contractor'), false);
            }
        }).catch((error) => {
            props.handleToast(error.message, true);
        }).finally(() => {
            props.onClose();
        });
    }

    const handleSubmit = () => {
        if (contractor?.isRestrictedContractor) {
            editRestriction();
        } else {
            handleSave();
        }
    }

    const editRestriction = async () => {
        setDisabled(true);
        const response = await props.editRestriction(restrictionId, applicantName, applicantEmail, reason, remarks)
        if(response.data.editContractorRestriction.isError || response.data.editContractorRestriction.value == 0) {
            props.handleToast(t('Error while editing restriction'), false);
            props.onClose();
        }else {
            props.handleToast(t('Restriction has been edited successfully'), true);
            props.onClose();
        }
    }

    const resolveLegalForm = useResolveName(props.legalForms, { translate: true });

    return (
        <div className='restrict-contractor-dialog'>
            {!isSaving? <>
                <G label={t('Contractor.Name')}>
                    <input
                        disabled={true}
                        className='c'
                        type='text'
                        value={contractor?.name}
                    />
                </G>
                <G label={t('CIF')}>
                    <input
                        disabled={true}
                        className='c'
                        type='text'
                        value={contractor?.code}
                    />
                </G>
                <G label={t('Legal form')}>
                    <input
                        disabled={true}
                        className='c'
                        type='text'
                        value={resolveLegalForm(contractor?.legalFormId)}
                    />
                </G>
                <G label={t('Provider code')}>
                    <input
                        disabled={true}
                        className='c'
                        type='text'
                        value={contractor?.providerCode}
                    />
                </G>
                <G label={t('Contact person')}>
                    <input
                        disabled={true}
                        className='c'
                        type='text'
                        value={contractor?.contactPerson}
                    />
                </G>
                <G label={t('contractor.invitation.email')}>
                    <input
                        disabled={true}
                        className='c'
                        type='text'
                        value={contractor?.email}
                    />
                </G>
                <G label={t('Phone')}>
                    <input
                        disabled={true}
                        className='c'
                        type='text'
                        value={contractor?.phone}
                    />
                </G>
                <G label={t('Applicant')}>
                    <input
                        disabled={!canModify}
                        className='c'
                        type='text'
                        value={applicantName}
                        onChange={(e) =>  setApplicantName(e.target.value)}
                    />
                </G>
                <G label={t('Applicant email')}>
                    <input
                        disabled={!canModify}
                        className='c'
                        type='email'
                        value={applicantEmail}
                        onChange={(e) => setApplicantEmail(e.target.value)}
                    />
                </G>
                <G label={t('Date')}>
                    <input
                        disabled={restrictionId !== undefined}
                        className='c'
                        type='date'
                        value={date.split('T')[0]}
                        onChange={(e) => setDate(e.target.value)}
                    />
                </G>
                <G label={t('Motive')} className='restriction-reason'>
                    <textarea
                        disabled={!canModify}
                        className='c'
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                    />
                </G>
                <G label={t('Remarks')} className='restriction-remarks'>
                    <textarea
                        disabled={!canModify}
                        className='c'
                        value={remarks}
                        onChange={(e) => setRemarks(e.target.value)}
                    />
                </G>
                <div className='c' />
                <div className='r'>
                    <span className='e' />

                    { canModify && <button
                        className='primary'
                        disabled={disabled || isSaving}
                        onClick={handleSubmit}>
                        {t('Save')}
                    </button>}
                </div>
            </> : loading.renderBox()

        }
        </div>
    )
}